import type React from "react";
import { useState } from "react";
import { Search, ChevronDown, Filter, X } from "lucide-react";
import Button from "./ButtonComponent";

interface FilterOption {
  label: string;
  value: string;
}

interface FilterComponentProps {
  onSearch?: (query: string) => void;
  onRiskLevelChange?: (value: string) => void;
  onAgeGroupChange?: (value: string) => void;
  onGenderChange?: (value: string) => void;
}

export default function FilterComponent({
  onSearch,
  onRiskLevelChange,
  onAgeGroupChange,
  onGenderChange,
}: FilterComponentProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [selectedRiskLevel, setSelectedRiskLevel] = useState("Risk level");
  const [selectedAgeGroup, setSelectedAgeGroup] = useState("Age group");
  const [selectedGender, setSelectedGender] = useState("Gender");
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  // Track actual filter values to determine if filters are active
  const [riskLevelValue, setRiskLevelValue] = useState("all");
  const [ageGroupValue, setAgeGroupValue] = useState("all");
  const [genderValue, setGenderValue] = useState("all");

  const riskLevelOptions: FilterOption[] = [
    { label: "All Risk Levels", value: "all" },
    { label: "High Risk", value: "poor" },
    { label: "Medium Risk", value: "average" },
    { label: "Low Risk", value: "good" },
  ];

  const ageGroupOptions: FilterOption[] = [
    { label: "All Ages", value: "all" },
    { label: "18-35", value: "18-35" },
    { label: "36-50", value: "36-50" },
    { label: "51-65", value: "51-65" },
    { label: "65+", value: "65+" },
  ];

  const genderOptions: FilterOption[] = [
    { label: "All Genders", value: "all" },
    { label: "Male", value: "male" },
    { label: "Female", value: "female" },
    { label: "Other", value: "other" },
  ];

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    onSearch?.(value);
  };

  const handleDropdownToggle = (dropdown: string) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  // Check if any filter is active
  const hasActiveFilters = () => {
    return (
      searchQuery !== "" ||
      riskLevelValue !== "all" ||
      ageGroupValue !== "all" ||
      genderValue !== "all"
    );
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery("");
    setRiskLevelValue("all");
    setAgeGroupValue("all");
    setGenderValue("all");
    setSelectedRiskLevel("Risk level");
    setSelectedAgeGroup("Age group");
    setSelectedGender("Gender");
    setActiveDropdown(null);
    setIsMobileFilterOpen(false);

    // Call all callbacks with default values
    onSearch?.("");
    onRiskLevelChange?.("all");
    onAgeGroupChange?.("all");
    onGenderChange?.("all");
  };

  const handleOptionSelect = (
    dropdown: string,
    option: FilterOption,
    setter: (value: string) => void,
    callback?: (value: string) => void,
    valueSetter?: (value: string) => void
  ) => {
    setter(option.label);
    valueSetter?.(option.value);
    callback?.(option.value);
    setActiveDropdown(null);
  };

  const renderDropdown = (
    id: string,
    selectedValue: string,
    options: FilterOption[],
    setter: (value: string) => void,
    callback?: (value: string) => void,
    valueSetter?: (value: string) => void
  ) => (
    <div className="relative">
      <Button
        onClick={() => handleDropdownToggle(id)}
        className="flex items-center space-x-2 !justify-between !min-w-[120px]"
      >
        <span>{selectedValue}</span>
        <ChevronDown className="h-4 w-4" />
      </Button>

      {activeDropdown === id && (
        <div className="absolute top-full left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {options.map((option) => (
            <button
              key={option.value}
              onClick={() =>
                handleOptionSelect(id, option, setter, callback, valueSetter)
              }
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-white border-b border-gray-200">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-4 gap-4">
          {/* Search Input */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search patients"
                value={searchQuery}
                onChange={handleSearchChange}
                className="pl-10 pr-4 py-2 rounded-lg bg-[#F3F4F6] focus:outline-none text-sm font-medium text-gray-900 placeholder-black"
              />
            </div>
          </div>

          {/* Desktop Filter Buttons - Hidden on mobile */}
          <div className="hidden md:flex items-center space-x-3">
            {renderDropdown(
              "risk-level",
              selectedRiskLevel,
              riskLevelOptions,
              setSelectedRiskLevel,
              onRiskLevelChange,
              setRiskLevelValue
            )}
            {renderDropdown(
              "age-group",
              selectedAgeGroup,
              ageGroupOptions,
              setSelectedAgeGroup,
              onAgeGroupChange,
              setAgeGroupValue
            )}
            {renderDropdown(
              "gender",
              selectedGender,
              genderOptions,
              setSelectedGender,
              onGenderChange,
              setGenderValue
            )}
            {hasActiveFilters() && (
              <Button
                onClick={handleClearFilters}
                className="flex items-center space-x-2 !p-2 !px-3"
              >
                <X className="h-4 w-4" />
                <span className="text-sm">Clear Filters</span>
              </Button>
            )}
          </div>

          {/* Mobile Filter Button - Shown only on mobile */}
          <div className="md:hidden">
            <Button
              onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
              className="flex items-center space-x-2 !p-2 !px-3"
            >
              <Filter className="h-4 w-4" />
              <span className="text-sm">Filters</span>
            </Button>
          </div>
        </div>

        {/* Mobile Filter Dropdown */}
        {isMobileFilterOpen && (
          <div className="md:hidden border-t border-gray-200 py-4 space-y-3">
            {renderDropdown(
              "risk-level",
              selectedRiskLevel,
              riskLevelOptions,
              setSelectedRiskLevel,
              onRiskLevelChange,
              setRiskLevelValue
            )}
            {renderDropdown(
              "age-group",
              selectedAgeGroup,
              ageGroupOptions,
              setSelectedAgeGroup,
              onAgeGroupChange,
              setAgeGroupValue
            )}
            {renderDropdown(
              "gender",
              selectedGender,
              genderOptions,
              setSelectedGender,
              onGenderChange,
              setGenderValue
            )}
            {hasActiveFilters() && (
              <Button
                onClick={handleClearFilters}
                className="flex items-center space-x-2 !p-2 !px-3 w-full !justify-center"
              >
                <X className="h-4 w-4" />
                <span className="text-sm">Clear Filters</span>
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Overlay for dropdowns */}
      {(activeDropdown || isMobileFilterOpen) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setActiveDropdown(null);
            setIsMobileFilterOpen(false);
          }}
        />
      )}
    </div>
  );
}
